/* ====================================================================
   LIHL Flair FX — animated border effects library (no dependency).
   Grown out of the game page's GWG flame border (which stays exclusive
   to game-winning goals and lives in game.html).

   Renders an animated effect around a card for every
     <canvas class="fx-flair-canvas" data-fx="electric|smoke|siren|shine|
             gotteem|flame|sparkle" data-color="#rrggbb"></canvas>
   The canvas should be absolutely positioned with a negative inset inside
   the card's container; the effect hugs the border of the target card:
   the container's `.fx-flair-target` child, or `.event-content` for game
   timeline events, or the canvas's parent element.

   Most effects are WebGL fragment shaders; `gotteem` is a 2D-canvas
   animation of the gotteem face peeking out from behind the card (put
   that canvas UNDER the card: z-index below the card content).

   A single rAF loop scans the DOM, so it works as Vue/plain JS adds and
   removes canvases. Respects prefers-reduced-motion (renders nothing —
   callers should keep a static CSS fallback on the card itself).

   window.FlairFX.attach(cardEl, fx, color) / .detach(cardEl) mount a
   preview on any positioned card (used by the Flair Studio).
   ==================================================================== */
(function () {
    var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var SIZE = 0.28; // effect thickness multiplier — flair hugs the border tightly
    var BASE_REACH = 22;
    var DEFAULT_COLOR = [1.0, 0.35, 0.05];
    var MODES = {
        flame: 0, electric: 1, sparkle: 2, smoke: 3, siren: 4, shine: 5,
        pulse: 6, frost: 7, ooze: 8, rain: 9, comet: 10, glitch: 11, bubbles: 12, scan: 13
    };

    var VERT = 'attribute vec2 p; void main(){ gl_Position = vec4(p,0.,1.); }';
    var FRAG = [
    'precision highp float;',
    'uniform float u_time; uniform vec2 u_rectCenter; uniform vec2 u_rectHalf;',
    'uniform float u_radius; uniform float u_reach; uniform vec3 u_color; uniform vec3 u_color2; uniform int u_mode;',
    'float hash(vec2 p){ p=fract(p*vec2(123.34,456.21)); p+=dot(p,p+45.32); return fract(p.x*p.y); }',
    'float noise(vec2 p){ vec2 i=floor(p),f=fract(p); f=f*f*(3.-2.*f);',
    '  float a=hash(i),b=hash(i+vec2(1,0)),c=hash(i+vec2(0,1)),d=hash(i+vec2(1,1));',
    '  return mix(mix(a,b,f.x),mix(c,d,f.x),f.y); }',
    'float fbm(vec2 p){ float v=0.,a=0.5; for(int i=0;i<5;i++){ v+=a*noise(p); p*=2.; a*=0.5; } return v; }',
    'float sdRoundRect(vec2 p, vec2 b, float r){ vec2 q=abs(p)-b+r; return min(max(q.x,q.y),0.)+length(max(q,0.))-r; }',
    'void main(){',
    '  vec2 frag = gl_FragCoord.xy;',
    '  float d = sdRoundRect(frag - u_rectCenter, u_rectHalf, u_radius);',
    '  float reach = max(u_reach, 1.0);',
    '  float st = u_time;',
    '  vec3 base = u_color;',
    '  if (u_mode == 0) {',
    // ---- flame: fbm noise drifting upward, posterized cel bands ----
    '    vec2 np = frag*0.04; np.y -= st*1.6;',
    '    vec2 w = vec2(fbm(np+vec2(0.,st*0.4)), fbm(np+7.1));',
    '    float n = pow(clamp(fbm(np + w*1.2), 0., 1.), 1.5);',
    '    float dist = abs(d) / reach;',
    '    float fire = n*1.5 - dist;',
    '    float thr = 0.13;',
    '    float m = smoothstep(thr-0.02, thr+0.03, fire);',
    '    if (m <= 0.01) discard;',
    '    float q = floor(clamp((fire-thr)*2.4, 0., 1.)*4.0)/3.0;',
    '    vec3 col = base*0.50;',
    '    col = mix(col, base*0.95,                step(0.30,q));',
    '    col = mix(col, mix(base,vec3(1.),0.28),  step(0.62,q));',
    '    col = mix(col, mix(base,vec3(1.),0.60),  step(0.95,q));',
    '    gl_FragColor = vec4(col, m);',
    '  } else if (u_mode == 1) {',
    // ---- electric: jagged ridged-noise bolts crawling the border ----
    '    if (abs(d) > reach*1.4) discard;',
    '    float ang = atan(frag.y - u_rectCenter.y, frag.x - u_rectCenter.x);',
    '    float crawl = st*2.2;',
    '    float r1 = fbm(vec2(ang*3.0 + crawl, st*0.7));',
    '    float ridge = 1.0 - abs(2.0*r1 - 1.0);',
    '    float jag = (fbm(frag*0.05 + vec2(0., crawl)) - 0.5) * reach * 0.9;',
    '    float dj = abs(d + jag);',
    '    float band = 1.0 - smoothstep(0.0, reach*0.55, dj);',
    '    float flicker = 0.55 + 0.45*noise(vec2(floor(st*14.0), floor(ang*4.0)));',
    '    float bolt = pow(ridge, 3.0) * flicker;',
    '    float m = band * clamp(bolt*1.6, 0., 1.);',
    '    if (m <= 0.03) discard;',
    '    float core = 1.0 - smoothstep(0.0, reach*0.18, dj);',
    '    vec3 col = mix(base*0.6, base, clamp(bolt,0.,1.));',
    '    col = mix(col, mix(base, vec3(1.), 0.75), core*bolt);',
    '    gl_FragColor = vec4(col, clamp(m*1.4, 0., 1.));',
    '  } else if (u_mode == 2) {',
    // ---- sparkle: twinkling star points in a band hugging the border ----
    '    float bandw = reach*1.1;',
    '    float band = 1.0 - smoothstep(bandw*0.35, bandw, abs(d));',
    '    if (band <= 0.02) discard;',
    '    vec2 gp = frag*0.09;',
    '    vec2 cell = floor(gp); vec2 f = fract(gp) - 0.5;',
    '    float rnd = hash(cell);',
    '    vec2 off = vec2(hash(cell+3.1), hash(cell+9.7)) - 0.5;',
    '    float pd = length(f - off*0.6);',
    '    float tw = pow(0.5 + 0.5*sin(st*(2.5 + 5.0*rnd) + rnd*40.0), 6.0);',
    '    float star = (1.0 - smoothstep(0.0, 0.16 + 0.12*rnd, pd)) * tw;',
    '    vec2 fr = f - off*0.6;',
    '    float rays = (1.0-smoothstep(0.006,0.05,abs(fr.x)))*(1.0-smoothstep(0.,0.3,abs(fr.y)));',
    '    rays += (1.0-smoothstep(0.006,0.05,abs(fr.y)))*(1.0-smoothstep(0.,0.3,abs(fr.x)));',
    '    star = max(star, rays*tw*0.8);',
    '    float m = band * star;',
    '    if (m <= 0.03) discard;',
    '    vec3 col = mix(base, vec3(1.), pow(star,2.0)*0.7);',
    '    gl_FragColor = vec4(col, clamp(m*1.6, 0., 1.));',
    '  } else if (u_mode == 3) {',
    // ---- smoke: soft tinted billows curling up off the card edges ----
    '    if (abs(d) > reach*2.2) discard;',
    '    vec2 np = frag*0.022; np.y -= st*0.5;',
    '    vec2 w = vec2(fbm(np + vec2(0., st*0.18)), fbm(np + 4.7));',
    '    float n = fbm(np + w*1.6);',
    '    float dist = abs(d) / (reach*2.0);',
    '    float body = n - dist*0.85;',
    '    float m = smoothstep(0.06, 0.5, body) * 0.6;',
    '    if (m <= 0.02) discard;',
    '    float depth = clamp(n*1.4, 0., 1.);',
    '    vec3 col = mix(base*0.25, mix(base, vec3(1.), 0.25), depth);',
    '    gl_FragColor = vec4(col, m);',
    '  } else if (u_mode == 4) {',
    // ---- siren: two opposed beacon beams sweeping the border ----
    '    float band = 1.0 - smoothstep(reach*0.15, reach*1.05, abs(d));',
    '    if (band <= 0.02) discard;',
    '    float ang = atan(frag.y - u_rectCenter.y, frag.x - u_rectCenter.x);',
    '    float rot = st*3.4;',
    '    float beamA = pow(max(cos(ang - rot), 0.), 10.0);',
    '    float beamB = pow(max(cos(ang - rot - 3.14159), 0.), 10.0);',
    '    float strobe = 0.75 + 0.25*step(0.5, fract(st*4.0));',
    '    float m = band * clamp((beamA + beamB) * strobe, 0., 1.);',
    '    if (m <= 0.02) discard;',
    '    vec3 col = base*beamA + u_color2*beamB;',
    '    float core = 1.0 - smoothstep(0., reach*0.35, abs(d));',
    '    col = mix(col, mix(col, vec3(1.), 0.55), core);',
    '    gl_FragColor = vec4(col, clamp(m*1.5, 0., 1.));',
    '  } else if (u_mode == 5) {',
    // ---- shine: constant sleek rim + a specular glint sweeping around ----
    '    float rim = 1.0 - smoothstep(0., reach*0.5, abs(d));',
    '    if (rim <= 0.02) discard;',
    '    float ang = atan(frag.y - u_rectCenter.y, frag.x - u_rectCenter.x);',
    '    float sweep = pow(max(cos(ang - st*1.4), 0.), 28.0);',
    '    float trail = pow(max(cos(ang - st*1.4 + 0.45), 0.), 60.0)*0.5;',
    '    float m = rim * (0.22 + (sweep + trail)*1.3);',
    '    vec3 col = mix(base, vec3(1.), clamp(sweep*0.75, 0., 1.));',
    '    gl_FragColor = vec4(col, clamp(m, 0., 1.));',
    '  } else if (u_mode == 6) {',
    // ---- pulse: EKG double-beat throb of the rim ----
    '    float s = fract(st*0.9);',
    '    float beat = exp(-pow((s-0.10)*16.,2.)) + 0.75*exp(-pow((s-0.34)*16.,2.));',
    '    float rim = 1.0 - smoothstep(0., reach*(0.45 + 0.55*beat), abs(d));',
    '    float m = rim * (0.20 + beat*1.1);',
    '    if (m <= 0.02) discard;',
    '    vec3 col = mix(base*0.8, mix(base, vec3(1.), 0.45), beat);',
    '    gl_FragColor = vec4(col, clamp(m, 0., 1.));',
    '  } else if (u_mode == 7) {',
    // ---- frost: crystalline rime with drifting glints ----
    '    if (abs(d) > reach*1.3) discard;',
    '    float r1 = fbm(frag*0.09);',
    '    float ridge = pow(1.0 - abs(2.0*r1 - 1.0), 2.0);',
    '    float band = 1.0 - smoothstep(0., reach*(0.6 + 0.5*ridge), abs(d));',
    '    float glint = pow(noise(frag*0.13 + vec2(st*0.5, -st*0.3)), 6.0)*2.0;',
    '    float m = band * (0.30 + ridge*0.55 + glint);',
    '    if (m <= 0.03) discard;',
    '    vec3 icy = mix(base, vec3(1.), 0.45);',
    '    vec3 col = mix(base*0.65, icy, ridge);',
    '    col = mix(col, vec3(1.), clamp(glint, 0., 1.)*0.8);',
    '    gl_FragColor = vec4(col, clamp(m, 0., 1.));',
    '  } else if (u_mode == 8) {',
    // ---- ooze: thick blobby slime dripping downward ----
    '    vec2 np = vec2(frag.x*0.045, frag.y*0.03 + st*0.35);',
    '    float n = fbm(np);',
    '    float sag = (n - 0.5) * reach * 1.6;',
    '    float dd = d + max(sag, 0.)*step(frag.y, u_rectCenter.y);',   // sag mostly below center
    '    float body = 1.0 - smoothstep(reach*0.2, reach*1.1, abs(dd + sag*0.4));',
    '    float m = smoothstep(0.25, 0.6, body);',
    '    if (m <= 0.03) discard;',
    '    float gloss = pow(clamp(n*1.6 - 0.4, 0., 1.), 3.0);',
    '    vec3 col = mix(base*0.45, base, clamp(n*1.5, 0., 1.));',
    '    col = mix(col, mix(base, vec3(1.), 0.55), gloss);',
    '    gl_FragColor = vec4(col, m*0.9);',
    '  } else if (u_mode == 9) {',
    // ---- rain: digital dashes streaming down the border band ----
    '    float band = 1.0 - smoothstep(reach*0.3, reach*1.25, abs(d));',
    '    if (band <= 0.02) discard;',
    '    float colw = 7.0;',
    '    float colId = floor(frag.x / colw);',
    '    float speed = 60.0 + hash(vec2(colId, 1.7))*90.0;',
    '    float yy = frag.y + st*speed;',
    '    float cell = floor(yy / 11.0);',
    '    float on = step(0.45, hash(vec2(colId, cell)));',
    '    float trail = fract(yy / 11.0);',
    '    float m = band * on * trail * 1.2;',
    '    if (m <= 0.03) discard;',
    '    vec3 col = mix(base*0.6, mix(base, vec3(1.), 0.6), step(0.8, trail));',
    '    gl_FragColor = vec4(col, clamp(m, 0., 1.));',
    '  } else if (u_mode == 10) {',
    // ---- comet: bright head orbiting with a long fading sparkle tail ----
    '    float band = 1.0 - smoothstep(reach*0.2, reach*1.1, abs(d));',
    '    if (band <= 0.02) discard;',
    '    float ang = atan(frag.y - u_rectCenter.y, frag.x - u_rectCenter.x);',
    '    float behind = fract((ang + st*1.6) / 6.28318);',           // 0 at head, grows behind
    '    float tail = pow(1.0 - behind, 6.0);',
    '    float head = pow(1.0 - behind, 60.0)*1.6;',
    '    float dust = step(0.6, hash(floor(frag*0.18))) * tail * 0.9;',
    '    float m = band * clamp(tail*0.85 + head + dust, 0., 1.4);',
    '    if (m <= 0.03) discard;',
    '    vec3 col = mix(base, vec3(1.), clamp(head, 0., 1.)*0.8);',
    '    gl_FragColor = vec4(col, clamp(m, 0., 1.));',
    '  } else if (u_mode == 11) {',
    // ---- glitch: slices of the rim tearing sideways and flickering ----
    '    float row = floor(frag.y / 6.0);',
    '    float tick = floor(st*9.0);',
    '    float jit = (hash(vec2(row, tick)) - 0.5) * reach * 1.5 * step(0.6, hash(vec2(row, tick+7.0)));',
    '    float dj = abs(d + jit);',
    '    float band = 1.0 - smoothstep(reach*0.15, reach*0.55, dj);',
    '    float alive = 0.20 + 0.60*step(0.35, hash(vec2(row, tick+3.0)));',
    '    float m = band * alive * 0.9;',
    '    if (m <= 0.03) discard;',
    '    float hot = step(0.85, hash(vec2(row, tick+11.0)));',
    '    vec3 col = mix(base, mix(u_color2, vec3(1.), 0.4), hot*0.7);',
    '    gl_FragColor = vec4(col, clamp(m, 0., 1.));',
    '  } else if (u_mode == 12) {',
    // ---- bubbles: fizzy rings rising through the border band ----
    '    float band = 1.0 - smoothstep(reach*0.4, reach*1.4, abs(d));',
    '    if (band <= 0.02) discard;',
    '    vec2 gp = vec2(frag.x*0.075, frag.y*0.075);',
    '    float colId = floor(gp.x);',
    '    gp.y -= st*(0.5 + 0.5*hash(vec2(colId, 4.2)));',
    '    vec2 cellv = floor(gp); vec2 f = fract(gp) - 0.5;',
    '    float rnd = hash(cellv);',
    '    if (rnd < 0.35) discard;',
    '    vec2 off = (vec2(hash(cellv+2.2), hash(cellv+8.8)) - 0.5)*0.5;',
    '    off.x += sin(st*3.0 + rnd*20.0)*0.08;',                     // wobble
    '    float rad = 0.10 + 0.14*rnd;',
    '    float ring = 1.0 - smoothstep(0.015, 0.05, abs(length(f - off) - rad));',
    '    float m = band * ring * (0.5 + 0.5*rnd);',
    '    if (m <= 0.03) discard;',
    '    vec3 col = mix(base, vec3(1.), 0.35 + 0.3*rnd);',
    '    gl_FragColor = vec4(col, clamp(m, 0., 1.));',
    '  } else {',
    // ---- scan: laser line sweeping down the card, lighting the rim ----
    '    float rim = 1.0 - smoothstep(0., reach*0.45, abs(d));',
    '    if (rim <= 0.02) discard;',
    '    float span = u_rectHalf.y + reach*2.0;',
    '    float yLoc = frag.y - u_rectCenter.y;',
    '    float scanY = span - fract(st*0.45)*span*2.0;',             // top -> bottom, loops
    '    float beam = exp(-pow((yLoc - scanY)/(reach*0.5), 2.0));',
    '    float after = exp(-pow((yLoc - scanY)/(reach*2.2), 2.0))*0.35;',
    '    float m = rim * (0.16 + (beam + after)*1.5);',
    '    vec3 col = mix(base, vec3(1.), clamp(beam, 0., 1.)*0.7);',
    '    gl_FragColor = vec4(col, clamp(m, 0., 1.));',
    '  }',
    '}'].join('\n');

    function setup(canvas) {
        var gl = canvas.getContext('webgl', { premultipliedAlpha: false, antialias: true });
        if (!gl) return null;
        function sh(type, src) {
            var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
            if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) return null;
            return s;
        }
        var vs = sh(gl.VERTEX_SHADER, VERT), fs = sh(gl.FRAGMENT_SHADER, FRAG);
        if (!vs || !fs) return null;
        var prog = gl.createProgram();
        gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
        gl.useProgram(prog);
        var buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        var lp = gl.getAttribLocation(prog, 'p');
        gl.enableVertexAttribArray(lp); gl.vertexAttribPointer(lp, 2, gl.FLOAT, false, 0, 0);
        var U = {};
        ['u_time', 'u_rectCenter', 'u_rectHalf', 'u_radius', 'u_reach', 'u_color', 'u_color2', 'u_mode'].forEach(function (n) {
            U[n] = gl.getUniformLocation(prog, n);
        });
        return { gl: gl, U: U };
    }

    function parseColor(hex) {
        if (!hex) return DEFAULT_COLOR;
        var m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
        if (!m) return DEFAULT_COLOR;
        var n = parseInt(m[1], 16);
        return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
    }

    function targetCard(canvas) {
        var p = canvas.parentElement;
        if (!p) return null;
        var t = p.querySelector('.fx-flair-target');
        if (t) return t;
        var ev = canvas.closest ? canvas.closest('.event') : null;
        if (ev) {
            var card = ev.querySelector('.event-content');
            if (card) return card;
        }
        return p;
    }

    function cardRectOn(canvas) {
        var card = targetCard(canvas);
        if (!card) return null;
        var cr = canvas.getBoundingClientRect();
        if (cr.width === 0 || cr.height === 0) return null;
        var rect = card.getBoundingClientRect();
        return { canvasRect: cr, cardRect: rect };
    }

    // ---------------- WebGL border effects ----------------

    function renderGL(canvas, t) {
        if (canvas._fx === undefined) canvas._fx = setup(canvas);
        var fx = canvas._fx;
        if (!fx) return;
        var rects = cardRectOn(canvas);
        if (!rects) return;
        var cr = rects.canvasRect, rect = rects.cardRect;
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var w = Math.round(cr.width * dpr), h = Math.round(cr.height * dpr);
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
        if (!canvas._color) {
            canvas._color = parseColor(canvas.getAttribute('data-color'));
            // siren beam B: the color's complement (red -> cyan, blue -> amber)
            canvas._color2 = [1 - canvas._color[0], 1 - canvas._color[1], 1 - canvas._color[2]];
        }
        if (canvas._mode === undefined) canvas._mode = MODES[canvas.getAttribute('data-fx')] || 0;
        var cx = (rect.left + rect.width / 2 - cr.left) * dpr;
        var cyTop = (rect.top + rect.height / 2 - cr.top) * dpr;
        var gl = fx.gl, U = fx.U;
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.uniform1f(U.u_time, t);
        gl.uniform2f(U.u_rectCenter, cx, canvas.height - cyTop);
        gl.uniform2f(U.u_rectHalf, (rect.width / 2) * dpr, (rect.height / 2) * dpr);
        gl.uniform1f(U.u_radius, 8 * dpr);
        gl.uniform1f(U.u_reach, BASE_REACH * SIZE * dpr);
        gl.uniform3f(U.u_color, canvas._color[0], canvas._color[1], canvas._color[2]);
        gl.uniform3f(U.u_color2, canvas._color2[0], canvas._color2[1], canvas._color2[2]);
        gl.uniform1i(U.u_mode, canvas._mode);
        gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    // ---------------- driver loop ----------------

    function render(canvas, t) {
        renderGL(canvas, t);
    }

    var running = false;
    function loop(now) {
        var t = now / 1000;
        var list = document.querySelectorAll('.fx-flair-canvas');
        for (var i = 0; i < list.length; i++) render(list[i], t);
        requestAnimationFrame(loop);
    }
    function ensureLoop() {
        if (running || REDUCED) return;
        running = true;
        requestAnimationFrame(loop);
    }

    // Preview helper: mount/unmount an effect canvas on any positioned card
    function attach(cardEl, fxName, color) {
        detach(cardEl);
        if (REDUCED) return;
        var c = document.createElement('canvas');
        c.className = 'fx-flair-canvas';
        c.setAttribute('data-fx', fxName);
        c.setAttribute('data-color', color || '');
        c.style.cssText = 'position:absolute;inset:-22px;width:calc(100% + 44px);height:calc(100% + 44px);pointer-events:none;z-index:2;';
        cardEl.classList.add('fx-flair-target');
        if (getComputedStyle(cardEl).position === 'static') cardEl.style.position = 'relative';
        var host = cardEl.parentElement;
        if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
        host.appendChild(c);
        cardEl._fxCanvas = c;
    }
    function detach(cardEl) {
        if (cardEl._fxCanvas) {
            cardEl._fxCanvas.remove();
            cardEl._fxCanvas = null;
        }
        cardEl.classList.remove('fx-flair-target');
    }

    ensureLoop();
    window.FlairFX = { attach: attach, detach: detach, MODES: MODES };
})();
